import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/roles";
import { StatusBadge, PURPOSE_LABELS, fullAddress } from "@/lib/cases";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/app/AppShell";
import { Box, EvidenceList, VerdictBadge, FIELD_STATUS } from "@/components/app/CaseWork";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const sel = "h-10 rounded-md border border-gray-200 bg-white px-3 text-sm";
const btn = "rounded-full bg-oath-ink px-4 py-2 text-xs font-semibold text-white disabled:opacity-50";
const act = async (fn, ok, reload) => {
  try {
    await fn();
    toast.success(ok);
    reload();
  } catch (e) {
    toast.error(formatApiError(e));
  }
};

export const openFile = async (id) => {
  try {
    const res = await api.get(`/files/${id}`, { responseType: "blob" });
    window.open(URL.createObjectURL(res.data), "_blank");
  } catch (e) {
    toast.error("Could not open file.");
  }
};

const EvidenceForm = ({ id, reload }) => {
  const [f, setF] = useState({ source: "", finding: "", weight: "supports" });
  return (
    <div className="mt-3 grid gap-2" data-testid="ops-evidence-form">
      <div className="grid gap-2 sm:grid-cols-[1fr_140px]">
        <Input placeholder="Source (e.g. Change-of-address registry)" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} data-testid="ops-evidence-source" />
        <select value={f.weight} onChange={(e) => setF({ ...f, weight: e.target.value })} className={sel} data-testid="ops-evidence-weight"><option value="supports">Supports</option><option value="conflicts">Conflicts</option><option value="neutral">Neutral</option></select>
      </div>
      <Textarea rows={2} placeholder="What the source shows, with dates" value={f.finding} onChange={(e) => setF({ ...f, finding: e.target.value })} data-testid="ops-evidence-finding" />
      <button className={`${btn} w-fit`} onClick={() => act(() => api.post(`/ops/cases/${id}/evidence`, f), "Evidence added", () => { setF({ source: "", finding: "", weight: "supports" }); reload(); })} data-testid="ops-evidence-add">Add evidence</button>
    </div>
  );
};

const VerdictForm = ({ id, reload }) => {
  const [v, setV] = useState("verified");
  const [s, setS] = useState("");
  return (
    <div className="grid gap-2" data-testid="ops-verdict-form">
      <select value={v} onChange={(e) => setV(e.target.value)} className={sel} data-testid="ops-verdict-select"><option value="verified">Verified</option><option value="discrepant">Discrepant</option><option value="unverifiable">Unverifiable</option></select>
      <Textarea rows={3} placeholder="Summary for the client" value={s} onChange={(e) => setS(e.target.value)} data-testid="ops-verdict-summary" />
      <button className={`${btn} w-fit`} onClick={() => act(() => api.post(`/ops/cases/${id}/verdict`, { verdict: v, summary: s }), "Sent to QA", reload)} data-testid="ops-verdict-submit">Submit for QA</button>
    </div>
  );
};

const FieldPanel = ({ c, reload, canAssign }) => {
  const [agents, setAgents] = useState([]);
  const [pick, setPick] = useState("");
  useEffect(() => {
    if (canAssign) api.get("/admin/agents").then(({ data }) => setAgents(data)).catch(() => {});
  }, [canAssign]);
  const open = ["unassigned", "assigned", "attempted"].includes(c.field.status);
  return (
    <Box title={`FIELD VISIT · ${c.field.city.toUpperCase()}`} testid="ops-field">
      <p className="text-sm" data-testid="ops-field-status">{FIELD_STATUS[c.field.status]}{c.field.agent_name ? ` · ${c.field.agent_name}` : ""}</p>
      {c.field.attempts.map((a, i) => (
        <div key={i} className="mt-2 rounded-xl bg-oath-bg/60 p-3 text-xs">
          <b>Attempt {i + 1}: {a.outcome.replace("_", " ")}</b> · {fmtDate(a.at)} {a.lat && `· ${a.lat}, ${a.lng}`}<p className="mt-1">{a.notes}</p>
          <div className="mt-1 flex gap-2">{a.photo_ids.map((p, j) => <button key={p} onClick={() => openFile(p)} className="text-oath-accent underline">Photo {j + 1}</button>)}</div>
        </div>
      ))}
      {canAssign && open && ["consent_given", "in_progress"].includes(c.status) && (
        <div className="mt-3 flex gap-2">
          <select value={pick} onChange={(e) => setPick(e.target.value)} className={sel} data-testid="ops-agent-select"><option value="">Choose agent…</option>{agents.map((a) => <option key={a.id} value={a.id}>{a.name || a.email}</option>)}</select>
          <button disabled={!pick} className={btn} onClick={() => act(() => api.post(`/ops/cases/${c.id}/assign`, { agent_id: pick }), "Assigned", reload)} data-testid="ops-agent-assign">Assign</button>
        </div>
      )}
    </Box>
  );
};

const Docs = ({ files, reload }) => (
  <Box title="FILES" testid="ops-files">
    {files.length === 0 && <p className="text-sm text-oath-muted">No files.</p>}
    {files.map((f) => (
      <div key={f.id} className="mb-2 rounded-xl bg-oath-bg/60 p-3 text-xs" data-testid={`ops-file-${f.id}`}>
        <div className="flex flex-wrap justify-between gap-2"><button onClick={() => openFile(f.id)} className="font-semibold text-oath-accent underline">{f.kind === "document" ? f.doc_type?.replace("_", " ") : f.kind.replace("_", " ")} · {f.original_filename}</button>
          {f.review_status && <span data-testid={`ops-file-status-${f.id}`}>{f.review_status.toUpperCase()}{f.archived ? " · ARCHIVED" : ""}</span>}</div>
        {f.auto_checks?.map((ch) => <div key={ch.check} className={ch.ok ? "text-emerald-700" : "text-red-700"}>{ch.ok ? "✓" : "✗"} {ch.check}</div>)}
        {f.kind === "document" && f.review_status === "pending" && (
          <div className="mt-2 flex gap-2">
            <button className="rounded-full bg-emerald-600 px-3 py-1 text-white" onClick={() => act(() => api.post(`/ops/documents/${f.id}/review`, { status: "accepted" }), "Accepted", reload)} data-testid={`ops-doc-accept-${f.id}`}>Accept</button>
            <button className="rounded-full border px-3 py-1" onClick={() => { const r = window.prompt("Reason for rejecting"); if (r) act(() => api.post(`/ops/documents/${f.id}/review`, { status: "rejected", reason: r }), "Rejected", reload); }} data-testid={`ops-doc-reject-${f.id}`}>Reject</button>
          </div>
        )}
      </div>
    ))}
  </Box>
);

const CasePanel = ({ id, user, onChanged }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/ops/cases/${id}`).then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader2 className="h-5 w-5 animate-spin" />;
  const c = d.case;
  const reload = () => { load(); onChanged(); };
  const working = ["consent_given", "in_progress"].includes(c.status);
  const isQa = ["super_admin", "qa_reviewer"].includes(user.platform_role);
  return (
    <div className="grid gap-4" data-testid="ops-case-panel">
      <Box title={`CASE ${c.case_number}`}>
        <div className="flex flex-wrap items-center gap-2"><StatusBadge status={c.status} testid="ops-case-status" /><span className="text-xs text-oath-muted">{c.org_name} · {PURPOSE_LABELS[c.permissible_purpose]} · {c.check_type === "records_field" ? "Records + field" : "Records"}</span></div>
        <p className="mt-2 font-display text-lg">{c.subject.first_name} {c.subject.last_name}</p>
        <p className="text-sm text-oath-secondary">{fullAddress(c.address)}</p>
        {c.household?.length > 0 && <p className="mt-1 text-xs text-oath-secondary">Declared household: {c.household.map((h) => `${h.name} (${h.relationship})`).join(", ")}</p>}
        {c.consent?.signed_at && <p className="mt-1 text-xs text-oath-muted">Consent recorded {fmtDate(c.consent.signed_at)}</p>}
      </Box>
      <Box title="EVIDENCE" testid="ops-evidence"><EvidenceList items={c.evidence} />{working && <EvidenceForm id={c.id} reload={reload} />}</Box>
      {c.field && <FieldPanel c={c} reload={reload} canAssign={user.platform_role === "super_admin"} />}
      <Docs files={d.files} reload={reload} />
      {working && <Box title="VERDICT"><VerdictForm id={c.id} reload={reload} /></Box>}
      {c.verdict && <Box title="SUBMITTED VERDICT" testid="ops-submitted-verdict"><VerdictBadge v={c.verdict.verdict} /><p className="mt-2 text-sm">{c.verdict.summary}</p><p className="mt-1 text-xs text-oath-muted">By {c.verdict.by}{c.verdict.qa_by ? ` · QA ${c.verdict.qa_by}` : ""}</p>
        {c.status === "qa_review" && isQa && (
          <div className="mt-3 flex gap-2">
            <button className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white" onClick={() => act(() => api.post(`/ops/cases/${c.id}/qa`, { approve: true }), "Delivered", reload)} data-testid="ops-qa-approve">Approve & deliver</button>
            <button className="rounded-full border px-4 py-2 text-xs" onClick={() => { const note = window.prompt("What needs more work?") || ""; act(() => api.post(`/ops/cases/${c.id}/qa`, { approve: false, note }), "Returned", reload); }} data-testid="ops-qa-return">Return</button>
          </div>
        )}
      </Box>}
    </div>
  );
};

export default function Ops() {
  const { user } = useAuth();
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get(`/ops/cases?status=${status}`).then(({ data }) => setRows(data)).catch((e) => toast.error(formatApiError(e))), [status]);
  useEffect(() => { load(); }, [load]);
  return (
    <AppShell eyebrow="— OPERATIONS" title="Verification work queue">
      <select value={status} onChange={(e) => setStatus(e.target.value)} className={sel} data-testid="ops-status-filter">
        <option value="">Open + delivered</option><option value="consent_given">Ready to start</option><option value="in_progress">In progress</option><option value="qa_review">QA review</option><option value="delivered">Delivered</option>
      </select>
      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_1.4fr]">
        <div className="grid content-start gap-2" data-testid="ops-queue">
          {rows === null && <Loader2 className="h-5 w-5 animate-spin" />}
          {rows?.length === 0 && <p className="text-sm text-oath-muted" data-testid="ops-empty">Nothing in this queue.</p>}
          {rows?.map((c) => (
            <button key={c.id} onClick={() => setOpen(c.id)} data-testid={`ops-row-${c.id}`}
              className={`rounded-xl border bg-white px-4 py-3 text-left transition-colors ${open === c.id ? "border-oath-ink" : "border-gray-200 hover:border-gray-400"}`}>
              <div className="flex items-center justify-between gap-2"><span className="font-mono text-xs text-oath-muted">{c.case_number}</span><StatusBadge status={c.status} testid={`ops-row-status-${c.id}`} /></div>
              <div className="mt-1 text-sm font-medium">{c.subject.first_name} {c.subject.last_name} <span className="text-xs text-oath-muted">· {c.org_name}{c.field ? ` · field ${c.field.city}` : ""}</span></div>
            </button>
          ))}
        </div>
        <div>{open ? <CasePanel key={open} id={open} user={user} onChanged={load} /> : <p className="text-sm text-oath-muted">Select a case.</p>}</div>
      </div>
    </AppShell>
  );
}
